import type { CurvePoint } from "@/services/standardCurveService";

/**
 * Reading a breed standard at an arbitrary age.
 *
 * A breeder guide publishes one value per WEEK; a flock's production report has
 * one row per DAY. Something has to bridge the two, and the choice is visible
 * to the reader:
 *
 *  - A step function (hold last week's value for seven days) draws a staircase
 *    the breeder guide does not claim. On a body-weight chart the flock's own
 *    smooth line crossing a staircase reads as a series of alarms.
 *  - LINEAR INTERPOLATION between the two bracketing weeks is what every
 *    published breeder chart draws, and it is what this does.
 *
 * Outside the curve's range the answer is `null`, NOT the nearest endpoint.
 * A hen-day curve starts at lay onset, so a pullet at week 8 has no standard —
 * flat-lining it at the week-18 value would invent a target the flock is
 * "failing", and clamping at the far end would claim the guide covers an age
 * it stops at. Apex renders null as a gap in the line, which is the honest
 * picture: no standard here.
 */
export function curveValueAt(points: CurvePoint[], ageWeeks: number): number | null {
  if (points.length === 0 || !Number.isFinite(ageWeeks)) return null;

  // Callers hand over whatever the API returned; ordering is the caller's
  // problem only if we assume it. Sorting a handful of points is free.
  const pts = [...points].sort((a, b) => a.age_weeks - b.age_weeks);

  const first = pts[0];
  const last = pts[pts.length - 1];
  if (ageWeeks < first.age_weeks || ageWeeks > last.age_weeks) return null;

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (ageWeeks < a.age_weeks || ageWeeks > b.age_weeks) continue;
    const span = b.age_weeks - a.age_weeks;
    // Duplicate weeks would divide by zero. The server rejects them, but this
    // function is handed data over a network boundary.
    if (span === 0) return a.value;
    return a.value + ((ageWeeks - a.age_weeks) * (b.value - a.value)) / span;
  }
  return last.value;
}

/**
 * Age in weeks as a FRACTION, from days. A flock 88 days old is at week 12.57,
 * not week 12 — rounding down before interpolating would reintroduce exactly
 * the staircase the interpolation exists to avoid.
 */
export const weeksFromDays = (ageDays: number): number => ageDays / 7;
